import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/shadcn/select'
import { HelpLabel } from '@/components/common/help/HelpLabel'
import { HelpText } from '@/components/common/help/HelpText'
import { incentivationSources } from '@/lib/action-modules/registry'
import { helpKey } from '@/lib/action-modules/help'
import type { IncentivationSourceState } from '@/lib/action-modules/registry/types'
import type { IncentivationSourceType } from '@/api/model'

interface Props {
  value: IncentivationSourceState
  onChange: (value: IncentivationSourceState) => void
}

export default function IncentivationSourceTab({ value, onChange }: Props) {
  const { t } = useTranslation('action-modules-wizard')
  const definition = useMemo(
    () => incentivationSources.find(i => i.type === value.selectedIncentivationSourceType),
    [value.selectedIncentivationSourceType],
  )

  const onChangeType = (newType: IncentivationSourceType) => {
    const def = incentivationSources.find(i => i.type === newType)
    onChange({
      selectedIncentivationSourceType: newType,
      incentivationSource: { ...(def?.defaultValue ?? {}), type: newType },
    })
  }

  return (
    <div className="flex flex-col gap-4 p-4">
      <h3 className="text-sm font-semibold">{t('tabIncentivationSource.common')}</h3>
      <div className="flex max-w-md flex-col gap-2">
        <HelpLabel helpKey={helpKey('incentivationSourceType')}>
          {t('tabIncentivationSource.incentivationSourceType')}
        </HelpLabel>
        <Select
          value={value.selectedIncentivationSourceType ?? undefined}
          onValueChange={v => onChangeType(v as IncentivationSourceType)}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder={t('tabIncentivationSource.incentivationSourceType')} />
          </SelectTrigger>
          <SelectContent>
            {incentivationSources.map(s => (
              <SelectItem key={s.type} value={s.type}>
                {t(s.nameKey)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {definition?.helpKey && <HelpText helpKey={definition.helpKey} />}
      </div>

      {definition && (
        <div className="pt-1">
          {definition.editor({
            value: value.incentivationSource,
            onChanged: v => onChange({ ...value, incentivationSource: v }),
          })}
        </div>
      )}
    </div>
  )
}
